import { Session, CreateSessionRequest, DrinkRequest, UpdateSessionRequest, ApiErrorCode } from '../api/types';
import { API_URLS } from '../config/api';

/**
 * A request the API answered with an error status. Network failures never
 * reach the server, so they surface as the TypeError fetch rejects with.
 */
export class ApiRequestError extends Error {
  constructor(message: string, readonly status: number, readonly code: ApiErrorCode | null) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

/** Builds the error for a non-OK response, keeping the "<label>: <status>"
 *  message callers already match on and adding the typed error code. */
const requestFailed = async (response: Response, label: string): Promise<ApiRequestError> => {
  let code: ApiErrorCode | null = null;
  try {
    const body = await response.json();
    code = body?.error?.code ?? null;
  } catch {
    // Not JSON (e.g. a gateway error page): the status is all we have
  }
  return new ApiRequestError(`${label}: ${response.status}`, response.status, code);
};

/**
 * Whether a failed request is worth retrying: no response at all (offline,
 * dropped connection), throttling, or a server-side failure. Any other 4xx
 * will fail the same way every time.
 */
export const isRetryableError = (error: unknown): boolean => {
  if (error instanceof ApiRequestError) {
    return isRetryableStatus(error.status);
  }
  return true;
};

export const isRetryableStatus = (status: number): boolean =>
  status === 408 || status === 429 || status >= 500;

const SESSION_ID_KEY = 'sessionId';
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;

const readSessionCookie = (): string | null => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${SESSION_ID_KEY}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

export class SessionService {
  /**
   * Get the session ID from local storage, falling back to the cookie copy
   * if local storage has been cleared
   */
  static getSessionId(): string | null {
    const stored = localStorage.getItem(SESSION_ID_KEY);
    if (stored) {
      return stored;
    }
    const fromCookie = readSessionCookie();
    if (fromCookie) {
      localStorage.setItem(SESSION_ID_KEY, fromCookie);
    }
    return fromCookie;
  }

  /**
   * Store the session ID in local storage plus a cookie for cheap redundancy
   */
  static setSessionId(sessionId: string): void {
    localStorage.setItem(SESSION_ID_KEY, sessionId);
    document.cookie = `${SESSION_ID_KEY}=${encodeURIComponent(sessionId)}; max-age=${COOKIE_MAX_AGE_SECONDS}; path=/; SameSite=Lax`;
  }

  /**
   * Clear the session ID from local storage and the cookie
   */
  static clearSessionId(): void {
    localStorage.removeItem(SESSION_ID_KEY);
    document.cookie = `${SESSION_ID_KEY}=; max-age=0; path=/; SameSite=Lax`;
  }

  /**
   * Fetch session data by session ID
   */
  static async getSession(sessionId: string): Promise<Session> {
    try {
      const response = await fetch(API_URLS.SESSION(sessionId));
      
      if (!response.ok) {
        throw await requestFailed(response, 'Failed to fetch session');
      }

      const session: Session = await response.json();
      return session;
    } catch (error) {
      console.error('Error fetching session:', error);
      throw error;
    }
  }

  /**
   * Create a new session
   */
  static async createSession(crawlId: string): Promise<Session> {
    try {
      const requestBody: CreateSessionRequest = {
        crawlId
      };

      const response = await fetch(API_URLS.CREATE_SESSION(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw await requestFailed(response, 'Failed to create session');
      }

      const session: Session = await response.json();
      return session;
    } catch (error) {
      console.error('Error creating session:', error);
      throw error;
    }
  }

  /**
   * Record a drink at a pub. `scannedAt` is when the scan really happened, for
   * scans queued while offline; the server clamps it to a plausible range.
   */
  static async recordDrink(pubId: string, sessionId: string, scannedAt?: string): Promise<Session> {
    try {
      const requestBody: DrinkRequest = {
        pubId,
        sessionId,
        ...(scannedAt ? { scannedAt } : {}),
      };

      const response = await fetch(API_URLS.DRINK(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        keepalive: true,
      });

      if (!response.ok) {
        throw await requestFailed(response, 'Failed to record drink');
      }

      const session: Session = await response.json();
      return session;
    } catch (error) {
      console.error('Error recording drink:', error);
      throw error;
    }
  }

  /**
   * Update session data (e.g., contender name)
   */
  static async updateSession(sessionId: string, updates: UpdateSessionRequest): Promise<Session> {
    try {
      const response = await fetch(API_URLS.SESSION(sessionId), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        throw await requestFailed(response, 'Failed to update session');
      }

      const session: Session = await response.json();
      return session;
    } catch (error) {
      console.error('Error updating session:', error);
      throw error;
    }
  }

  /**
   * Finalise a session (stop the clock). The server sets the end time to the
   * last scan, so lingering before pressing the button costs nothing.
   */
  static async finishSession(sessionId: string, contenderName?: string): Promise<Session> {
    try {
      const response = await fetch(API_URLS.FINISH_SESSION(sessionId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contenderName !== undefined ? { contenderName } : {}),
      });

      if (!response.ok) {
        throw await requestFailed(response, 'Failed to finish session');
      }

      const session: Session = await response.json();
      return session;
    } catch (error) {
      console.error('Error finishing session:', error);
      throw error;
    }
  }

  /**
   * Look up a session by its human-readable resume code
   */
  static async resumeByCode(code: string): Promise<Session> {
    const response = await fetch(API_URLS.RESUME_SESSION(code.trim().toUpperCase()));

    if (!response.ok) {
      throw await requestFailed(response, 'Failed to resume session');
    }

    const session: Session = await response.json();
    return session;
  }

  /**
   * Check if a session exists and is still live
   */
  static async validateSession(sessionId: string): Promise<boolean> {
    try {
      const session = await this.getSession(sessionId);
      const now = new Date();
      const expiresAt = new Date(session.expiresAt);

      return now < expiresAt && !session.isFinalised;
    } catch (error) {
      console.error('Error validating session:', error);
      return false;
    }
  }
}
